import axios from 'axios';
import { CLIENT_ID_KEY, getClientId, isOwnApiRequest } from '@/helpers/client-id';
import { SESSION_TRANSPORT_COOKIE, SESSION_TRANSPORT_HEADER } from 'shared/schemas/auth/session.schema';
import { shouldReloadOnStorageChange } from '@/helpers/session-sync';

/**
 * A sessão do web vive num cookie HttpOnly (`fw_session`) que o JavaScript não lê: um XSS não
 * consegue levar o token embora. O navegador manda o cookie sozinho para a nossa API; aqui fica só
 * o que a tela precisa — o usuário e o id da sessão (que não abre nada sem o cookie). Ver
 * docs/features/httponly-session-cookie.md.
 */
const SESSION_ID_KEY = 'SessionId';
/** Chaves de antes do cookie (token no localStorage e o identificador de dispositivo compartilhado). */
const LEGACY_KEYS = ['AccessToken', 'DeviceToken'];

export const BASE_URL = window.location.host === 'localhost:3100' ? 'http://localhost:3000' : window.location.protocol + '//' + window.location.host;
export const getSessionId = () => window.localStorage.getItem(SESSION_ID_KEY);
export const isAuthenticated = () => getSessionId() !== null && window.localStorage.getItem('id') !== null;
export const getType = () => window.localStorage.getItem('type');
export const getUserId = () => window.localStorage.getItem('id');
export const getUserName = () => window.localStorage.getItem('name');

/**
 * Prepara as requisições para a NOSSA API:
 * - `X-Client-Id`: o servidor amarra a sessão a ele no login e recusa a requisição que chega sem
 *   ele — é também a proteção contra CSRF da sessão em cookie;
 * - `withCredentials`: no dev o SPA (`:3100`) e a API (`:3000`) são origens diferentes, e sem isso
 *   o navegador não manda o cookie. Em produção é mesma origem e não muda nada;
 * - `sessionId`: carimbo de qual sessão fez a requisição, para o tratamento do 401 saber se outra
 *   aba trocou a sessão no meio (o cookie é das abas todas).
 * Para outros hosts (ViaCEP, Memed, S3) nada disso vai: o header força preflight de CORS e esses
 * serviços bloqueiam a chamada.
 */
export const attachClientId = config => {
	if (isOwnApiRequest(config.url, config.baseURL, BASE_URL)) {
		config.headers = config.headers || {};
		config.headers['X-Client-Id'] = getClientId();
		config.withCredentials = true;
		config.sessionId = getSessionId();
	}
	return config;
};

/** Headers do login (`step1` e `otp/verify`): pede a sessão em cookie HttpOnly. */
export const cookieSessionHeaders = () => ({ [SESSION_TRANSPORT_HEADER]: SESSION_TRANSPORT_COOKIE });

// O axios global (login e chamadas cruas). Instâncias criadas com `axios.create` NÃO herdam
// interceptors: cada uma registra o `attachClientId` (Api.js e as telas com instância própria).
axios.interceptors.request.use(attachClientId);

/**
 * Conta (clínica) do usuário logado, gravada no login. É só **lida**, nunca usada para autorizar
 * nada — quem decide acesso é o backend. Serve para separar dado local por conta, como a fila de
 * transcrição, que vive no navegador e não no servidor. `null` sem login.
 */
export const getAccountId = () => {
	const accountId = Number(window.localStorage.getItem('account_id'));
	return Number.isInteger(accountId) && accountId > 0 ? accountId : null;
};

export const login = data => {
	LEGACY_KEYS.forEach(key => window.localStorage.removeItem(key));
	window.localStorage.setItem(SESSION_ID_KEY, data.session_id);
	window.localStorage.setItem('account_id', data.account_id);
	window.localStorage.setItem('id', data.id);
	window.localStorage.setItem('type', data.type);
	window.localStorage.setItem('name', data.name);
	window.localStorage.setItem('is_doctor', data.is_doctor);
	window.localStorage.setItem('is_accountant', data.is_accountant);
	window.localStorage.setItem('allow_finance', data.allow_finance);
	window.localStorage.setItem('allow_all_agenda', data.allow_all_agenda);
	window.localStorage.setItem('allow_health_record', data.allow_health_record);
	window.localStorage.setItem('board_code', data.board_code);
	window.localStorage.setItem('allow_agenda', !!(data.allow_all_agenda || data.is_doctor));
	window.localStorage.setItem('allow_patient', data.allow_patient);
	window.localStorage.setItem('allow_accounting', data.allow_accounting);
	window.localStorage.setItem('allow_shop', data.allow_shop);
	window.localStorage.setItem('allow_settings', data.allow_settings);
	window.localStorage.setItem('allow_prescription', data.allow_prescription);
	window.localStorage.setItem('allow', data.allow);
	window.localStorage.setItem('memed_token', data.memed_token);
	window.localStorage.setItem('allow_inventory', data.allow_inventory);
	window.localStorage.setItem('allow_crm', data.allow_crm);
	window.localStorage.setItem('allow_report', data.allow_report);
	window.localStorage.setItem('allow_automations', data.allow_automations);
	window.localStorage.setItem('allow_delete_appointment', data.allow_delete_appointment);
	window.localStorage.setItem('allow_reopen_finished_appointment', data.allow_reopen_finished_appointment);
	window.localStorage.setItem('allow_special_actions', data.allow_special_actions);
	window.localStorage.setItem('allow_evolution_api', data.allow_evolution_api);
	window.localStorage.setItem('initial_page', data.initial_page || 'schedule');

	const isDoctor = data.type === 'DOCTOR' || data.is_doctor === true;
	const isReceptionist = data.type === 'RECEPTIONIST';

	let redirectTo;
	if (isDoctor) {
		switch (data.initial_page) {
			case 'attendance-queue':
				redirectTo = '/schedule';
				break;
			case 'service-queue':
				redirectTo = '/service-queue';
				break;
			default:
				redirectTo = '/agenda';
				break;
		}
	} else if (isReceptionist) {
		redirectTo = '/?select-room=true';
	} else {
		redirectTo = '/';
	}

	window.location.href = redirectTo;
};

// Limpa o localStorage preservando o `ClientId`, que é a identidade deste navegador: outra aba
// ainda aberta continua mandando o valor que leu, e trocá-lo aqui faria a sessão dela parecer vinda
// de outro navegador. A confiança do dispositivo no 2FA não mora mais aqui: é um cookie HttpOnly
// por usuário, que sobrevive ao logout de propósito.
export const clearStorageKeepingDevice = () => {
	const clientId = window.localStorage.getItem(CLIENT_ID_KEY);
	window.localStorage.clear();
	if (clientId) window.localStorage.setItem(CLIENT_ID_KEY, clientId);
};

// O cookie de sessão é das abas todas: quando outra aba sai ou entra com outro usuário, esta
// continuaria mostrando o anterior enquanto as requisições já vão como o novo (ver session-sync).
const authenticatedAtLoad = isAuthenticated();
window.addEventListener('storage', event => {
	if (shouldReloadOnStorageChange(event.key, SESSION_ID_KEY, authenticatedAtLoad, window.location.pathname)) window.location.reload();
});

/**
 * Encerra a sessão no servidor (`POST /api/auth/logout`). Sem isso o token continuaria
 * válido até o fim do dia depois de "Sair" — limpar o navegador não invalida cópia
 * nenhuma. Nunca rejeita e desiste em 3s: sair da conta não pode depender da rede.
 */
let loggingOut = false;

/** O usuário clicou em "Sair": um 401 que chegue agora é efeito do logout, não sessão expirada. */
export const isLoggingOut = () => loggingOut;

/** Janela para o `beforeunload` cancelar a navegação do logout antes de rearmarmos o 401. */
const LOGOUT_GRACE_MS = 5000;

export const revokeServerSession = () => {
	loggingOut = true;
	// O servidor revoga a sessão e apaga o cookie HttpOnly, que o JavaScript não alcança.
	const request = isAuthenticated()
		? axios
				.post(BASE_URL + '/api/auth/logout', {}, { timeout: 3000 })
				.then(() => undefined)
				.catch(() => undefined)
		: Promise.resolve();

	// Quem chama navega logo depois. Se um `beforeunload` (o LaudoModal com alteração não
	// salva) cancelar a navegação, a aba fica com a sessão já encerrada: sem rearmar, todo
	// 401 seguinte seria engolido e salvar falharia sem aviso nenhum.
	return request.then(() => {
		window.setTimeout(() => {
			loggingOut = false;
		}, LOGOUT_GRACE_MS);
	});
};

export const logout = () => {
	revokeServerSession().then(() => {
		clearStorageKeepingDevice();
		window.location.href = '/';
	});
};
